import { QueryClient } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { getSession, useSession } from 'next-auth/react';
import { useEffect, useMemo } from 'react';

import AuthExperience from '../components/_organisms/Auth/AuthExperience';
import MainLoader from '../components/Loader';
import useSignIn from '../hooks/useSignIn';
import { clearAuthSessionCookie } from '../utils/clearAuthSessionCookie';
import { getGeoFromHeaders } from '../utils/localeRegionResolver';
import { detectAndBestEffortSetLanguage } from '../utils/localisationUtils';
import { isSafeRedirect } from '../utils/navigator';
import { readPostAuthRedirect, savePostAuthRedirect } from '../utils/postAuthRedirect';
import { dehydrateReactQueryPrefetchedInfiniteQuery } from '../utils/react-query-utils';
import { resolveNewAuth } from '../utils/resolveNewAuth';
import { provisionFlags } from '../utils/ServerSideFeatureFlagUtils';

const Login = ({ newAuth, isNorway }: { newAuth?: boolean; isNorway?: boolean }) => {
  const { signIn } = useSignIn();
  const session = useSession();
  const router = useRouter();
  const sessionStatus = session.status;
  const redirectParam = router.query.redirect;

  const fromQuery = useMemo(
    () => (isSafeRedirect(redirectParam) ? redirectParam : null),
    [redirectParam],
  );
  const effectiveRedirect = useMemo(() => fromQuery ?? readPostAuthRedirect() ?? '/', [
    fromQuery,
  ]);

  // Persist an explicit ?redirect so it survives the OAuth round-trip. The write
  // is a side effect, so it belongs in an effect (not useMemo), and only runs
  // when a redirect was actually provided — otherwise a bare /login visit would
  // clobber a previously stored destination.
  useEffect(() => {
    if (fromQuery) savePostAuthRedirect(fromQuery);
  }, [fromQuery]);

  // An errored session (e.g. ProvisioningError, no idToken) is NOT a real login —
  // treat it as logged-out so "Try again" from /auth/error renders the form here
  // instead of bouncing the user away while sign-out is still settling.
  const isLoggedIn = session.status === 'authenticated' && !session.data?.error;

  useEffect(() => {
    if (isLoggedIn) {
      router.push(effectiveRedirect);
      return;
    }
    // Legacy B2C flow: bounce to the hosted login once the session has resolved
    // and the user isn't usably logged in — this covers `unauthenticated` AND an
    // errored session (which is `authenticated` but unusable), so the page never
    // stalls on the loader. Skipped when the new in-place experience is enabled.
    if (!newAuth && !isLoggedIn && session.status !== 'loading') {
      signIn({ callbackUrl: effectiveRedirect });
    }
  }, [router, session, sessionStatus, signIn, effectiveRedirect, newAuth, isLoggedIn]);

  // Only render the auth card once the session has resolved (not while `loading`),
  // so a returning user whose session is about to resolve as authenticated doesn't
  // see a flash of the form. An errored session counts as resolved-and-logged-out
  // here, so "Try again" from /auth/error still shows the form.
  if (newAuth && session.status !== 'loading' && !isLoggedIn) {
    return (
      <>
        <Head>
          <meta name="robots" content="noindex, nofollow" />
        </Head>
        <AuthExperience
          initialMode="login"
          callbackUrl={effectiveRedirect}
          isNorway={isNorway}
        />
      </>
    );
  }

  return (
    <>
      <Head>
        <meta name="robots" content="noindex, nofollow" />
      </Head>
      <MainLoader />
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async context => {
  context.res.setHeader(
    'Cache-Control',
    'no-store, no-cache, must-revalidate, proxy-revalidate',
  );
  const session = await getSession({ req: context.req });
  const redirectParam = context.query.redirect;
  const redirect = isSafeRedirect(redirectParam) ? redirectParam : '/';

  // Only a genuinely authenticated session redirects away; an errored session
  // (provisioning failure, no token) must still render the login form.
  if (session && !session.error) {
    // If user is already authenticated, redirect to the requested URL (or home)
    return {
      redirect: {
        permanent: false,
        destination: redirect,
      },
    };
  } else {
    // If user is not authenticated, show the login page with all necessary props.
    // An errored session (provisioning failure) must NOT reach the client: clear
    // its cookie and don't hydrate it — otherwise the global BackendServiceProvider
    // signs the user out to /auth/error and blocks retry here. Mirrors /auth/error.
    const erroredSession = !!session?.error;
    if (erroredSession) clearAuthSessionCookie(context.res);
    const usableSession = erroredSession ? null : session;
    const queryClient = new QueryClient();
    const flags = await provisionFlags(context, queryClient, usableSession);
    const flagOn = !!flags.unleash.toggles?.find(t => t.name === 'US_AUTH')?.enabled;
    const newAuth = resolveNewAuth(flagOn, context);
    // Feide is Norway's national SSO — surface it next to Google there instead
    // of tucked under "other options".
    const isNorway = getGeoFromHeaders(context.req.headers).country === 'NO';
    return {
      props: {
        ...flags,
        newAuth,
        isNorway,
        dehydratedState: dehydrateReactQueryPrefetchedInfiniteQuery(queryClient),
        ...(await detectAndBestEffortSetLanguage(context, { routeClass: 'app' })),
        ...(usableSession && { session: usableSession }),
      },
    };
  }
};

export default Login;
